import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
// import FormInput from '../../components/Forms/FormInput'; // Não está sendo usado
import Pagination from '../../components/UI/Pagination';
import { getApplicationById, getApplicationEmbeddings } from '../../services/applications/applicationsService';
import { ApplicationDto, EmbeddingDto } from '../../services/applications/types';

const EmbeddingsList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const [application, setApplication] = useState<ApplicationDto | null>(null);
  const [embeddings, setEmbeddings] = useState<EmbeddingDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [topK, setTopK] = useState<number>(10);
  const [similarityThreshold, setSimilarityThreshold] = useState<number>(0.75);
  const [selectedEmbedding, setSelectedEmbedding] = useState<EmbeddingDto | null>(null);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  const pageSize = 10;

  useEffect(() => {
    if (id) {
      fetchApplicationData(id);
      // Não carregamos embeddings automaticamente, apenas após uma pesquisa
    }
  }, [id, auth.user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Update URL when page changes
    setSearchParams({ page: currentPage.toString() });
  }, [currentPage, setSearchParams]);

  const fetchApplicationData = async (applicationId: string) => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to view application details.');
      return;
    }
    
    try {
      const appData = await getApplicationById(auth.user || null, applicationId);
      setApplication(appData);
    } catch (err) {
      console.error('Error fetching application:', err);
      setError('Failed to fetch application details.');
    }
  };

  const fetchEmbeddings = async (
    page: number, 
    query: string = '',
    top_k?: number,
    similarity_threshold?: number
  ) => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to search embeddings.');
      return;
    }
    
    if (!query || query.trim() === '') {
      setError('Por favor, informe um termo para pesquisa');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getApplicationEmbeddings(
        auth.user || null,
        id!, 
        page, 
        pageSize, 
        query, 
        top_k, 
        similarity_threshold
      );
      
      // Verificar se response e response.data existem antes de acessar propriedades
      if (response && response.data) {
        setEmbeddings(response.data);
        setTotalPages(response.total_pages || 1);
        setTotalItems(response.total_items || 0);
      } else {
        // Se não há dados, definir um array vazio
        setEmbeddings([]);
        setTotalPages(1);
        setTotalItems(0);
        setError('Nenhum embedding encontrado para esta pesquisa.');
      }
    } catch (err) {
      console.error('Error fetching embeddings:', err);
      setError('Falha ao buscar embeddings. Por favor, tente novamente.');
      // Garantir que embeddings seja sempre um array para evitar erros
      setEmbeddings([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    if (id) {
      setCurrentPage(1);
      setHasSearched(true);
      fetchEmbeddings(1, searchQuery, topK, similarityThreshold);
    }
  };
  
  const toggleAdvancedSearch = () => {
    setShowAdvancedSearch(!showAdvancedSearch);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEmbeddingClick = (embedding: EmbeddingDto) => {
    setSelectedEmbedding(embedding);
  };

  const handleCloseDetails = () => {
    setSelectedEmbedding(null);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text?: string, maxLength: number = 100) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Render a preview of vector data (first few elements)
  const renderVectorPreview = (vector?: number[]) => {
    if (!vector || vector.length === 0) return (
      <span className="text-sm text-gray-400">No vector data</span>
    );
    
    const previewSize = 3;
    const preview = vector.slice(0, previewSize);
    
    return (
      <div>
        <div className="text-xs text-gray-500 mb-1">
          Dim: {vector.length}
        </div>
        <div className="text-xs font-mono text-gray-600">
          [{preview.map(v => v.toFixed(3)).join(', ')}...]
        </div>
      </div>
    );
  };
  
  // Format the similarity score if available
  /* Função formatSimilarity não está sendo usada
  const formatSimilarity = (embedding: EmbeddingDto) => {
    if (embedding.metadata && embedding.metadata.similarity !== undefined) {
      const score = parseFloat(embedding.metadata.similarity);
      return (
        <div className="mt-1">
          <span 
            className={`px-2 py-1 text-xs rounded-full ${
              score > 0.9 ? 'bg-green-100 text-green-800' :
              score > 0.7 ? 'bg-blue-100 text-blue-800' :
              score > 0.5 ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}
          >
            {(score * 100).toFixed(1)}% match
          </span>
        </div>
      );
    }
    return null;
  };
  */

  const handleBackToApplication = () => {
    navigate(`/applications/${id}`);
  };

  return (
    <div>
      <PageHeader
        title={`Embeddings - ${application?.name || 'Application'}`}
        breadcrumbs={[
          { label: "Applications", to: "/applications" },
          { label: application?.name || 'Application', to: `/applications/${id}` },
          { label: "Embeddings" }
        ]}
        actions={
          <Button
            variant="secondary"
            onClick={handleBackToApplication}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Application
          </Button>
        }
      />

      <Card className="mb-6">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full">
              <div className="relative flex-grow sm:max-w-md">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search embeddings"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full"
                  />
                </div>
              </div>
              <div className="flex gap-2 self-end sm:self-auto">
                <Button 
                  variant="secondary" 
                  onClick={toggleAdvancedSearch}
                  size="sm"
                  type="button"
                >
                  {showAdvancedSearch ? 'Hide Filters' : 'Filters'}
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleSearch}
                  disabled={isLoading}
                >
                  Search
                </Button>
              </div>
            </div>
          </div>
          
          {showAdvancedSearch && (
            <div className="mb-4 p-4 bg-gray-50 rounded-md">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Advanced Search Options</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Top K Results
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={topK}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setTopK(value < 1 ? 1 : value > 100 ? 100 : value || 1);
                    }}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Maximum number of results to return (1-100)
                  </p>
                </div>
                <div>
                  <label className="flex items-center justify-between text-sm font-medium text-gray-700 mb-1">
                    <span>Similarity Threshold</span>
                    <span className="font-mono text-xs">
                      {similarityThreshold.toFixed(2)}
                    </span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer focus:outline-none"
                    value={similarityThreshold}
                    onChange={(e) => setSimilarityThreshold(parseFloat(e.target.value))}
                  />
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-gray-500">Less similar (0.0)</p>
                    <p className="text-xs text-gray-500">More similar (1.0)</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="px-6 pb-6">
            <div className="bg-red-50 text-red-700 p-4 rounded-md">
              {error}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : !hasSearched ? (
          <div className="text-center py-16 text-gray-500">
            <p className="text-lg font-medium mb-2">Digite um termo para pesquisar embeddings</p>
            <p className="text-sm max-w-lg mx-auto">
              Use o campo de busca acima para encontrar embeddings similares ao texto informado. 
              Você pode ajustar a quantidade de resultados e o limiar de similaridade nas opções avançadas.
            </p>
          </div>
        ) : embeddings.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <p className="text-lg font-medium mb-2">Nenhum embedding encontrado</p>
            <p className="text-sm max-w-lg mx-auto">
              Não foram encontrados embeddings que correspondam aos critérios de busca. 
              Tente modificar o texto da busca ou ajustar o limiar de similaridade nas opções avançadas.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Text</th>
                  <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Vector</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Match</th>
                  <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Created At</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {embeddings.map((embedding) => (
                  <tr 
                    key={embedding.id} 
                    className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                    onClick={() => handleEmbeddingClick(embedding)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {embedding.id ? embedding.id.substring(0, 8) + '...' : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{truncateText(embedding.text, 60)}</div>
                    </td>
                    <td className="hidden md:table-cell px-6 py-4">
                      {renderVectorPreview(embedding.vector)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {embedding.metadata && embedding.metadata.similarity !== undefined ? (
                        <span 
                          className={`inline-flex items-center px-2.5 py-1 text-xs rounded-full ${
                            parseFloat(embedding.metadata.similarity) > 0.9 ? 'bg-green-100 text-green-800' :
                            parseFloat(embedding.metadata.similarity) > 0.7 ? 'bg-blue-100 text-blue-800' :
                            parseFloat(embedding.metadata.similarity) > 0.5 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {(parseFloat(embedding.metadata.similarity) * 100).toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(embedding.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button 
                        variant="ghost" 
                        size="xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEmbeddingClick(embedding);
                        }}
                      >
                        <i className="fas fa-eye"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {hasSearched && embeddings.length > 0 && totalPages > 1 && (
          <div className="py-3 px-6 border-t border-gray-200">
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>

      {/* Embedding Details Modal */}
      {selectedEmbedding && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-semibold">Embedding Details</h3>
                {selectedEmbedding.metadata && selectedEmbedding.metadata.similarity !== undefined && (
                  <span 
                    className={`px-3 py-1 text-sm rounded-full ${
                      parseFloat(selectedEmbedding.metadata.similarity) > 0.9 ? 'bg-green-100 text-green-800' :
                      parseFloat(selectedEmbedding.metadata.similarity) > 0.7 ? 'bg-blue-100 text-blue-800' :
                      parseFloat(selectedEmbedding.metadata.similarity) > 0.5 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {(parseFloat(selectedEmbedding.metadata.similarity) * 100).toFixed(1)}% match
                  </span>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleCloseDetails}
              >
                <i className="fas fa-times"></i>
              </Button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Information</h4>
                <div className="bg-gray-50 p-4 rounded-md space-y-3 mb-6">
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">ID</span>
                    <span className="block text-sm font-mono mt-1">
                      {selectedEmbedding.id || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Application ID</span>
                    <span className="block text-sm font-mono mt-1">
                      {selectedEmbedding.application_id || 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500 block">Created At</span>
                      <span className="block text-sm mt-1">
                        {formatDate(selectedEmbedding.created_at)}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500 block">Updated At</span>
                      <span className="block text-sm mt-1">
                        {formatDate(selectedEmbedding.updated_at)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <h4 className="font-medium text-gray-900 mb-3">Text</h4>
                <div className="bg-gray-50 p-4 rounded-md mb-6 max-h-60 overflow-y-auto">
                  <div className="text-sm whitespace-pre-wrap">
                    {selectedEmbedding.text || 'No text available'}
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Metadata</h4>
                <div className="bg-gray-50 p-4 rounded-md mb-6 max-h-60 overflow-y-auto">
                  {selectedEmbedding.metadata && Object.keys(selectedEmbedding.metadata).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(selectedEmbedding.metadata).map(([key, value]) => (
                        <div key={key}>
                          <span className="text-sm font-medium text-gray-500">
                            {key}
                          </span>
                          <div className="mt-1 text-sm">
                            {typeof value === 'object' ? (
                              <pre className="text-xs overflow-x-auto">
                                {JSON.stringify(value, null, 2)}
                              </pre>
                            ) : (
                              <span>{String(value)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-sm text-gray-500">
                      No metadata available
                    </div>
                  )}
                </div>
                
                <h4 className="font-medium text-gray-900 mb-3">
                  Vector ({selectedEmbedding.vector?.length || 0} dimensions)
                </h4>
                <div className="bg-gray-50 p-4 rounded-md max-h-60 overflow-y-auto">
                  {selectedEmbedding.vector && selectedEmbedding.vector.length > 0 ? (
                    <div>
                      <div className="mb-3 text-xs text-gray-500">
                        Showing first 24 of {selectedEmbedding.vector.length} dimensions
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {selectedEmbedding.vector.slice(0, 24).map((value, index) => (
                          <div key={index} className="text-xs font-mono">
                            {index}: {value.toFixed(4)}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-sm text-gray-500">
                      No vector data available
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
              <Button 
                variant="primary" 
                onClick={handleCloseDetails}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbeddingsList;